import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enums ────────────────────────────────────────────────────────────────
export const VisitorPurpose = {
  FAMILY: 'FAMILY',
  FRIEND: 'FRIEND',
  DELIVERY: 'DELIVERY',
  SERVICE: 'SERVICE',
  OTHER: 'OTHER',
} as const;
export type VisitorPurpose = (typeof VisitorPurpose)[keyof typeof VisitorPurpose];

export const VisitorPassStatus = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',     // derived in DTO; never stored
} as const;
export type VisitorPassStatus =
  (typeof VisitorPassStatus)[keyof typeof VisitorPassStatus];

// Gate check-in code: 1 letter + 2 digits, e.g. "S78", "X23". The resident
// shows it to security, who types it in at the gate. Ambiguous letters
// (I, O) are excluded at generation time to avoid 1/0 confusion.
export const VISITOR_CODE_RE = /^[A-Z][0-9]{2}$/;
export const visitorCode = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.string().regex(VISITOR_CODE_RE, 'Code must be 1 letter + 2 digits (e.g. S78)'));

const PURPOSES = [
  VisitorPurpose.FAMILY,
  VisitorPurpose.FRIEND,
  VisitorPurpose.DELIVERY,
  VisitorPurpose.SERVICE,
  VisitorPurpose.OTHER,
] as const;
const STATUSES = [
  VisitorPassStatus.ACTIVE,
  VisitorPassStatus.CANCELLED,
  VisitorPassStatus.EXPIRED,
] as const;

// ─── Public shapes ────────────────────────────────────────────────────────
export const publicVisitorScan = z.object({
  id: uuid,
  pass_id: uuid,
  scanned_by_user_id: uuid,
  scanned_by_name: z.string().optional(),
  scanned_at: isoDatetime,
  notes: z.string().nullable(),
});
export type PublicVisitorScan = z.infer<typeof publicVisitorScan>;

export const publicVisitorPass = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  code: z.string(),                          // gate check-in code, e.g. "S78"
  host_user_id: uuid,
  host_name: z.string().optional(),
  unit_block: z.string().optional(),
  unit_number: z.string().optional(),
  visitor_name: z.string(),
  visitor_plate: z.string().nullable(),
  purpose: z.enum(PURPOSES),
  expected_at: isoDatetime,
  valid_from: isoDatetime,
  valid_until: isoDatetime,
  status: z.enum(STATUSES),                  // server fills derived EXPIRED here
  notes: z.string().nullable(),
  cancelled_at: isoDatetime.nullable(),
  cancelled_reason: z.string().nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
  scans: z.array(publicVisitorScan).optional(),
  scan_count: z.number().int().optional(),
});
export type PublicVisitorPass = z.infer<typeof publicVisitorPass>;

// ─── Resident requests ────────────────────────────────────────────────────
export const createVisitorPassRequest = z.object({
  visitor_name: z.string().min(1).max(100).trim(),
  visitor_plate: z.string().max(20).optional(),
  purpose: z.enum(PURPOSES).default(VisitorPurpose.OTHER),
  expected_at: isoDatetime,
  valid_until: isoDatetime,
  notes: z.string().max(500).optional(),
});
export type CreateVisitorPassRequest = z.infer<typeof createVisitorPassRequest>;

export const listVisitorPassesQuery = paginationQuery.extend({
  status: z.enum(STATUSES).optional(),
});
export type ListVisitorPassesQuery = z.infer<typeof listVisitorPassesQuery>;

export const cancelVisitorPassRequest = z.object({
  reason: z.string().max(200).optional(),
});

// ─── Security requests ────────────────────────────────────────────────────
// Security types the visitor's code at the gate. The server resolves it to the
// currently-valid pass in the cluster and records the entry.
export const scanVisitorRequest = z.object({
  code: visitorCode,
  notes: z.string().max(200).optional(),
});
export type ScanVisitorRequest = z.infer<typeof scanVisitorRequest>;

export const listVisitorScansQuery = paginationQuery.extend({
  since: isoDatetime.optional(),
});
export type ListVisitorScansQuery = z.infer<typeof listVisitorScansQuery>;
